import { createRoot } from "react-dom/client";
import { ErrorBoundary } from "./components/ErrorBoundary";
import App from "./App.tsx";
import "./index.css";

// --- Deferred initialization ---
// All store/side-effect setup runs AFTER imports are fully resolved,
// avoiding TDZ issues that can arise when bundlers reorder module code.

import { initStoreSync } from "./stores/storeSync";
import { configureMutationQueue, registerFlushListeners, recoverPendingMutations } from "./lib/mutationQueue";
import { useCanvasStore } from "./stores/canvasStore";
import { extractItemText } from "./lib/itemTextExtractor";
import { supabase } from "./integrations/supabase/client";

// Initialize sub-store sync (viewportStore, selectionStore, boardStore, itemStore)
initStoreSync();

// Initialize mutation queue for reliable data persistence
configureMutationQueue({
  onSavingStart: () => useCanvasStore.setState({ isSaving: true }),
  onSavingEnd: () => useCanvasStore.setState({ isSaving: false }),
  onContentChanged: (itemId: string) => {
    const item = useCanvasStore.getState().items.find(i => i.id === itemId);
    if (item) {
      const text = extractItemText({ item_type: item.item_type, content: item.content as Record<string, unknown> });
      if (text.length >= 5) {
        supabase.functions.invoke('generate-embedding', {
          body: { items: [{ itemId, content: text }] },
        }).catch(e => console.warn('[Embeddings] Background re-embedding failed:', e));
      }
    }
  },
});
registerFlushListeners();

// Recover any mutations that were persisted to IndexedDB but not flushed (crash recovery)
recoverPendingMutations().then(count => {
  if (count > 0) console.log(`[MutationQueue] Recovered ${count} pending mutations`);
});

// --- Prevent browser-level zoom so only the canvas zooms ---
// Ctrl+wheel / Cmd+wheel (desktop browsers)
document.addEventListener('wheel', (e) => {
  if (e.ctrlKey || e.metaKey) {
    e.preventDefault();
  }
}, { passive: false });

// Pinch-to-zoom gesture events (Safari)
document.addEventListener('gesturestart', (e) => e.preventDefault(), { passive: false } as EventListenerOptions);
document.addEventListener('gesturechange', (e) => e.preventDefault(), { passive: false } as EventListenerOptions);
document.addEventListener('gestureend', (e) => e.preventDefault(), { passive: false } as EventListenerOptions);

// Keyboard zoom shortcuts (Ctrl/Cmd + Plus/Minus/Zero)
document.addEventListener('keydown', (e) => {
  if ((e.ctrlKey || e.metaKey) && (e.key === '+' || e.key === '-' || e.key === '=' || e.key === '0')) {
    e.preventDefault();
  }
});

createRoot(document.getElementById("root")!).render(
  <ErrorBoundary>
    <App />
  </ErrorBoundary>
);
